import { useEffect, useState } from "react";
import { doc, getDoc } from 'firebase/firestore';
import { callServiceAPI } from '@/lib/firebase/functions';
import { Gift, Star, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from '@/components/ui/button';
import { db } from '@/config/firebase';
import { useAuthStore } from "@/stores/auth-store";
import { useAppStore } from "@/stores/app-store";
import { CreditOptionSkeleton } from "@/components/common/credit-loan-offer";

const CLAIM_CREDITS = 1000;

const appDocs = import.meta.glob('/src/content/v3/apps/*.md', {
    query: '?raw',
    import: 'default',
}) as Record<string, () => Promise<string>>;

function isMarketplaceListingUrl(url: string) {
    try {
        const { hostname, pathname } = new URL(url);
        if (hostname.endsWith('ulgebra.com')) return false;
        return pathname.replace(/\/+$/, '') !== '/marketplace' && pathname !== '/';
    } catch {
        return false;
    }
}

export function ReviewCreditsClaim() {
    const user = useAuthStore((s) => s.user);
    const extensionName = useAppStore((s) => s.extensionName);

    const [reviewUrl, setReviewUrl] = useState<string | null>(null);
    const [status, setStatus] = useState<'loading' | 'new' | 'sent' | 'approved' | 'rejected'>('loading');
    const [reviewOpened, setReviewOpened] = useState(false);
    const [saving, setSaving] = useState(false);
    const [linkChecked, setLinkChecked] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const hasAppDoc = !!extensionName && !!appDocs[`/src/content/v3/apps/${extensionName}.md`];

    useEffect(() => {
        const loader = appDocs[`/src/content/v3/apps/${extensionName}.md`];
        if(!loader) return;
        loader().then((md) => {
            const m = md.match(/^install_url:\s*"([^"]+)"/m);
            if(m && isMarketplaceListingUrl(m[1])) setReviewUrl(m[1]);
        }).finally(() => setLinkChecked(true));
    }, [extensionName]);

    useEffect(() => {
        if(!user || !extensionName) return;
        getDoc(doc(db, 'creditClaimRequests', `${user.uid}_${extensionName}`))
            .then((snap) => {
                if (!snap.exists()) return setStatus('new');
                const claimStatus = snap.data().status;
                setStatus(claimStatus === 'approved' || claimStatus === 'rejected' ? claimStatus : 'sent');
            })
            .catch(() => setStatus('new'));
    }, [user, extensionName]);

    async function sendClaim() {
        if(!user || !extensionName) return;
        setSaving(true);
        setError(null);
        try {
            const result = await callServiceAPI({ appCode: extensionName, url: 'submit_credit_claim', method: 'post', data: {} });
            const body = result.data as { error?: { message?: string } } | undefined;
            if (body?.error?.message) throw new Error(body.error.message);
            setStatus('sent');
        } catch (e) {
            setError((e as Error).message || 'Could not send your claim. Please try again.');
        } finally {
            setSaving(false);
        }
    }

    if (!user || !hasAppDoc) return null;
    if (!linkChecked || status === 'loading') return <CreditOptionSkeleton />;
    if (!reviewUrl) return null;
    if (status === 'approved') return null;

    if (status === 'rejected') {
        return (
            <div className="animate-fade-in-up flex-1 flex flex-col gap-2 lg:gap-3 rounded-xl border border-rose-400/40 bg-rose-50 dark:bg-rose-500/10 p-3 lg:p-4">
                <div className="flex h-8 w-8 lg:h-9 lg:w-9 items-center justify-center rounded-lg bg-rose-500/15">
                    <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                </div>
                <div className="space-y-1">
                    <p className="text-sm font-semibold text-rose-900 dark:text-rose-100">Claim not approved</p>
                    <p className="text-xs text-rose-800/80 dark:text-rose-200/80">
                        We couldn't find your review. Please contact support.
                    </p>
                </div>
            </div>
        );
    }

    if (status === 'sent') {
        return (
            <div className="animate-fade-in-up flex-1 flex flex-col gap-2 lg:gap-3 rounded-xl border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 p-3 lg:p-4">
                <div className="flex h-8 w-8 lg:h-9 lg:w-9 items-center justify-center rounded-lg bg-emerald-500/15">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="space-y-1">
                    <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Thanks for your review!</p>
                    <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80">
                        We're checking it and will add {CLAIM_CREDITS} credits soon.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in-up flex-1 flex flex-col gap-2 lg:gap-3 rounded-xl border border-border bg-muted/20 p-3 lg:p-4">
            <div className="flex items-center justify-between">
                <div className="flex h-8 w-8 lg:h-9 lg:w-9 items-center justify-center rounded-lg bg-amber-500/15">
                    <Gift className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                    Free
                </span>
            </div>
            <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">Get {CLAIM_CREDITS} free credits</p>
                <p className="text-xs text-muted-foreground">
                    Enjoying the app? Leave a quick review on the marketplace and we'll add {CLAIM_CREDITS} credits.
                </p>
            </div>
            {error && <p className="text-xs text-rose-600">{error}</p>}
            <div className="mt-auto grid grid-cols-2 gap-2 lg:grid-cols-1">
                <Button
                   variant='outline'
                   size="sm"
                   className="w-full"
                   onClick={ () => {
                    window.open(reviewUrl, '_blank', 'noopener,noreferrer');
                    setReviewOpened(true);
                   }}
                >
                    <Star className="h-4 w-4" />
                    Write a review
                </Button>
                <Button variant="outline" size="sm" className="w-full" disabled={!reviewOpened || saving} onClick={sendClaim}>
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Claim {CLAIM_CREDITS} credits
                </Button>
            </div>
        </div>
    );
}